<script setup lang="ts" generic="K extends LayerKpi">
/**
 * A task's KPIs, one row each (#900), and a role's (#901), which read the same way: the name, the light drawn with ZhKpiLight, the trend, where its readings come
 * from, and the team or role KPI it serves. The light and trend come from the same `@zerohuman/shared` derivation
 * every other layer's KPI uses (see KpiTargetCard.vue), so a task KPI reads exactly like a team's. A KPI that serves
 * nothing is flagged, not hidden: standing alone is allowed, but a lead should see which work moves no team number.
 *
 * A KPI nobody has signed off draws no light, whatever it holds: it says it is awaiting sign-off instead. A
 * signed-off KPI with no target draws none either, and says "No target yet" (#1696): it is measuring a baseline.
 * The `actions` slot lets a page put what the viewer may do beside each row.
 */
import { type KpiTrend, kpiLight, kpiTrend } from '@zerohuman/shared'
import { ZhKpiLight, ZhSystemLabel } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { NO_TARGET_TEXT } from '@/lib/kpi'
import { type LayerKpi, taskKpiTarget } from '@/task-kpis-api'

/** `linkTasks` links each name to its task's page, where it is signed off: for lists of more than one task's KPIs. */
const props = defineProps<{ kpis: readonly K[]; linkTasks?: boolean }>()

/** Each movement's arrow, as KpiTargetCard draws it: the arrow reports the move, the light judges it. */
const ARROWS: Record<KpiTrend['direction'], string> = { up: '↑', down: '↓', flat: '→' }

const rows = computed(() =>
    props.kpis.map(kpi => {
        const trend = kpiTrend(kpi.readings)
        return {
            kpi,
            light: kpi.signedOff ? kpiLight(taskKpiTarget(kpi), kpi.readings) : null,
            // No target, not no light: a KPI with a target and nothing read yet has no light either, but has a target.
            noTarget: kpi.targetValue === null,
            trend: kpi.signedOff && trend ? `${ARROWS[trend.direction]} ${trend.percent}%` : null,
            source: sourceText(kpi),
        }
    }),
)

/** Where the readings come from, in words; null when no source is set. */
function sourceText(kpi: LayerKpi): string | null {
    if (!kpi.sourceKind) {
        return null
    }
    const name = kpi.sourceName ?? (kpi.sourceKind === 'task' ? 'a task' : 'a person')
    return kpi.sourceKind === 'task' ? `Read by ${name}` : `Entered by ${name}`
}
</script>

<template>
	<ul class="space-y-1">
		<li
			v-for="row in rows"
			:key="row.kpi.id"
			:data-testid="`task-kpi-${row.kpi.id}`"
			class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
		>
			<ZhKpiLight v-if="row.light" :rag="row.light" />
			<RouterLink v-if="linkTasks" :to="`/tasks/${row.kpi.taskId}`" class="font-medium hover:underline">
				{{ row.kpi.name }}
			</RouterLink>
			<span v-else class="font-medium">{{ row.kpi.name }}</span>
			<ZhSystemLabel
				v-if="!row.kpi.signedOff"
				as="span"
				:data-testid="`task-kpi-awaiting-sign-off-${row.kpi.id}`"
			>
				Awaiting sign-off
			</ZhSystemLabel>
			<span
				v-else-if="row.noTarget"
				:data-testid="`task-kpi-no-target-${row.kpi.id}`"
				class="text-muted-foreground text-xs"
			>
				{{ NO_TARGET_TEXT }}
			</span>
			<span v-if="row.trend" class="text-muted-foreground text-xs">{{ row.trend }}</span>
			<span v-if="row.source" class="text-muted-foreground text-xs">{{ row.source }}</span>
			<span v-if="row.kpi.servesKpiId" class="text-muted-foreground text-xs">
				Serves {{ row.kpi.servesName ?? 'a team KPI' }}
			</span>
			<span v-else :data-testid="`task-kpi-unserved-${row.kpi.id}`" class="text-warning-amber text-xs">
				Serves no team KPI
			</span>
			<slot name="actions" :kpi="row.kpi" />
		</li>
	</ul>
</template>
